import { Tag, notification } from 'antd';
import { Modal, DataGrid, Icon, Button } from '@hydrodigital/design-system';
import React, { useEffect, useState } from 'react';
import KeplerService from '../../domain/Kepler/KeplerService';

const keplerService = new KeplerService();
type Iter = { iterationId: number; link: string | null; status?: string };

interface Props {
  showKeplerAnalysisModal: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  simulation: any;
}

export default function ModalKeplerAnalysis({
  showKeplerAnalysisModal,
  onCancel,
  onSuccess,
  simulation,
}: Props) {
  const [iterations, setIterations] = useState<Iter[]>([]);

  useEffect(() => {
    if (!simulation) return setIterations([]);

    const iters: Iter[] = [];
    const links = simulation.links || [];

    for (let i = 1; i <= simulation.numit; i++) {
      const found = links.find((l: any) => l.iterationId == i);
      iters.push(found ? { ...found } : { iterationId: i, link: null });
    }
    setIterations(iters);
  }, [simulation?.links]);

  const handleGenerateKepler = async (record: Iter) => {
    setIterations((prev) =>
      prev.map((it) =>
        it.iterationId === record.iterationId ? { ...it, status: 'IN PROGRESS' } : it,
      ),
    );

    const res = await keplerService.postGenerateKeplerAnimation(simulation.id, record.iterationId);

    if (res.success === 1) {
      notification.info({
        message: 'Request sent',
        description:
          'Your analysis is being performed, you will be able to get it in about 10 minutes.',
        placement: 'topRight',
        duration: 0,
      });
      onSuccess();
    } else {
      setIterations((prev) =>
        prev.map((it) =>
          it.iterationId === record.iterationId
            ? { iterationId: it.iterationId, link: null }
            : it,
        ),
      );
    }
  };

  const columns = [
    {
      accessorKey: 'iterationId',
      header: 'Iteration',
      cell: (cellContext: any) => {
        const row = cellContext.row.original as Iter;
        return <span>Iteration {row.iterationId}</span>;
      },
    },
    {
      accessorKey: 'actions',
      header: '',
      cell: (cellContext: any) => {
        const record = cellContext.row.original as Iter;

        if (record.status === 'IN PROGRESS') {
          return <Tag color="warning">IN PROGRESS</Tag>;
        }

        if (!record.link) {
          return (
            <button
              type="button"
              style={{ cursor: 'pointer', display: 'inline-flex', background: 'none', border: 'none', padding: 0 }}
              onClick={() => handleGenerateKepler(record)}
            >
              <Icon name="plus-circle" size="m" hoverable color="#232B36" />
            </button>
          );
        }

        return (
          <button
            type="button"
            style={{ cursor: 'pointer', display: 'inline-flex', background: 'none', border: 'none', padding: 0 }}
            onClick={() => window.open(record.link!, '_blank', 'noopener,noreferrer')}
          >
            <Icon name="eye" size="m" hoverable color="#232B36" />
          </button>
        );
      },
    },
  ];

  return (
    <Modal isOpen={showKeplerAnalysisModal} onClose={onCancel} size="xs" showCloseButton={false}>
      <Modal.Header>
        <Modal.Title text="Analysis" />
      </Modal.Header>

      <Modal.Content>
        <DataGrid
          columns={columns}
          data={iterations}
          bordered={true}
          hoverable={true}
          pagination={{ enabled: false }}
        />
      </Modal.Content>

      <Modal.Footer className="flex justify-end">
        <Button
          label="Close"
          variant="secondary"
          showIcon={false}
          size="small"
          onClick={onCancel}
        />
      </Modal.Footer>
    </Modal>
  );
}
